<template>
  <v-container>
    <div class="dashboard-header">
      <div class="welcome">
        <div class="d-flex">
          {{ $t("message.dashboard.title") }}
          <VideoButton
            class="ml-4"
            @click="showVideoTutorial(viewEnum.DASHBOARD)"
          />
        </div>
      </div>
      <div class="filters">
        <ui-button icon="icon-calendar" readonly>
          {{ $t("message.dashboard.daysRange", [numberOfDays]) }}
        </ui-button>
        <ui-button
          icon="icon-filter"
          color="primary"
          @click="filterDialog = true"
        >
          {{ $t("message.reviews.filter") }}
        </ui-button>
      </div>
    </div>
    <grid-layout
      class="dashboard-grid"
      :layout="layout"
      :col-num="3"
      :row-height="250"
      :margin="[0, 0]"
    >
      <grid-item
        class="dashboard-grid-item"
        v-for="widget in visibleWidgets"
        :key="widget.layout.i"
        :static="true"
        :x="widget.layout.x"
        :y="widget.layout.y"
        :w="widget.layout.w"
        :h="widget.layout.h"
        :i="widget.layout.i"
      >
        <component
          :is="widget.component.name"
          v-bind="widget.component.props"
          :query-params="queryParams"
        ></component>
      </grid-item>
    </grid-layout>

    <FilterDialog
      v-if="filterDialog"
      :gmb-addresses-ids="gmbAddressesIds"
      :cities="cities"
      :facilities-groups="facilitiesGroupsIds"
      :chosen-search-type="searchType"
      @setSearchType="searchType = $event"
      @change="onFiltersChange({ ...$event })"
      :handleClose="closeFilterDialog"
    />
  </v-container>
</template>

<script>
import RatingDistributionWidget from "@/components/Dashboard/RatingDistributionWidget";
import BestAddressesWidget from "@/components/Dashboard/BestAddressesWidget";
import WorstAddressesWidget from "@/components/Dashboard/WorstAddressesWidget";
import ValueWidget from "@/components/Dashboard/base/ValueWidget";
import QueriesWidget from "@/components/Dashboard/QueriesWidget";
import LowestQualityAddressesWidget from "@/components/Dashboard/LowestQualityAddressesWidget";
import FilterDialog from "@/components/Filter/dialog";
import VideoButton from "@/components/ui/VideoButton.vue";

import { FORMAT } from "@/config/date-format";
import { PERIOD } from "@/enums/period.enum";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { getRandomBetween } from "@/helpers/helpers";
import serviceHandler from "@/helpers/handlers/service";
import { DashboardService } from "@/services/dashoard.service";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import { videoTutorialsMixin } from "@/helpers/mixins/video-tutorials";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";

import { GridLayout, GridItem } from "vue-grid-layout";
import moment from "moment";
import { mapGetters, mapActions } from "vuex";

import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";

const defaultCount = {
  value: 0,
  change: 0
};

export default {
  name: "Dashboard",
  components: {
    ValueWidget,
    RatingDistributionWidget,
    BestAddressesWidget,
    WorstAddressesWidget,
    QueriesWidget,
    LowestQualityAddressesWidget,
    GridLayout,
    GridItem,
    FilterDialog,
    VideoButton
  },
  mixins: [gtmEngagementMixin, videoTutorialsMixin],
  data() {
    return {
      numberOfDays: 30,
      gmbAddressesIds: [],
      cities: [],
      facilitiesGroupsIds: [],
      searchType: null,
      filterDialog: false,
      loadingAddresses: false,
      loadingRatings: false,
      loadingStats: false,
      loadingPostsCount: false,
      averageRating: { ...defaultCount },
      ratingCount: { ...defaultCount },
      repliesPercentage: { ...defaultCount },
      ratingDistribution: { 1: "", 2: "", 3: "", 4: "", 5: "" },
      bestGmbAddresses: [],
      worstGmbAddresses: [],
      lowestQualityGmbAddresses: [],
      postsCount: { ...defaultCount },
      viewsCount: { ...defaultCount },
      queriesCount: { ...defaultCount },
      actionsWebsite: { ...defaultCount },
      actionsPhone: { ...defaultCount },
      actionsDrivingDirections: { ...defaultCount },
      queries: {
        direct: "",
        indirect: "",
        chain: ""
      }
    };
  },
  computed: {
    ...mapGetters({
      isDemoMode: "auth/isDemoMode"
    }),
    showIncreaseIndicator() {
      return isFeatureFlagEnabled(
        FEATURE_FLAG.SHOW_DASHBOARD_INCREASE_INDICATOR
      );
    },
    queryParams() {
      return {
        firstDateFrom: moment()
          .subtract(this.numberOfDays, PERIOD.DAYS)
          .format(FORMAT.DATE),
        firstDateTo: moment().format(FORMAT.DATE),
        secondDateFrom: moment()
          .subtract(this.numberOfDays * 2 + 1, PERIOD.DAYS)
          .format(FORMAT.DATE),
        secondDateTo: moment()
          .subtract(this.numberOfDays + 1, PERIOD.DAYS)
          .format(FORMAT.DATE),
        gmbAddressesIds: this.gmbAddressesIds,
        cities: this.cities,
        groups: this.facilitiesGroupsIds
      };
    },
    widgets() {
      return [
        {
          component: {
            name: "ValueWidget",
            props: {
              name: "ratingCount",
              value: this.ratingCount.value,
              increaseValue: this.ratingCount.change,
              loading: this.loadingRatings,
              actionHandler: () => this.goToReviewsAnalyst({}, "num_ratings")
            }
          },
          layout: { x: 0, y: 0, w: 1, h: 1, i: "0" },
          planRestrictions: this.canMonitorRatingsAndReports
        },
        {
          component: {
            name: "ValueWidget",
            props: {
              name: "averageRating",
              value: this.averageRating.value,
              increaseValue: this.averageRating.change,
              loading: this.loadingRatings,
              actionHandler: () => this.goToReviewsAnalyst({}, "avg_rating")
            }
          },
          layout: { x: 1, y: 0, w: 1, h: 1, i: "1" },
          planRestrictions: this.canMonitorRatingsAndReports
        },
        {
          component: {
            name: "ValueWidget",
            props: {
              name: "repliesPercentage",
              value: Math.round(this.repliesPercentage.value * 10) / 10,
              increaseValue: this.repliesPercentage.change,
              loading: this.loadingRatings,
              actionHandler: () => this.goToReviewsList({}, "reviews_replied")
            }
          },
          layout: { x: 2, y: 0, w: 1, h: 1, i: "2" },
          planRestrictions: this.canMonitorRatingsAndReports
        },
        {
          component: {
            name: "RatingDistributionWidget",
            props: {
              data: this.ratingDistribution,
              loading: this.loadingRatings,
              actionHandler: () =>
                this.goToReviewsAnalyst({}, "rating_distribution")
            }
          },
          layout: { x: 0, y: 1, w: 1, h: 3, i: "3" },
          planRestrictions: this.canMonitorRatingsAndReports
        },
        {
          component: {
            name: "BestAddressesWidget",
            props: {
              data: this.bestGmbAddresses,
              loading: this.loadingAddresses,
              actionHandler: () =>
                this.goToReviewsAnalyst(
                  {
                    gmbAddressesIds: this.bestGmbAddresses
                      .map(({ gmb_address_id }) => gmb_address_id)
                      .join(",")
                  },
                  "highest_rated_locations"
                )
            }
          },
          layout: { x: 1, y: 1, w: 2, h: 2, i: "4" },
          planRestrictions: this.canMonitorRatingsAndReports
        },
        {
          component: {
            name: "WorstAddressesWidget",
            props: {
              data: this.worstGmbAddresses,
              loading: this.loadingAddresses,
              actionHandler: () =>
                this.goToReviewsAnalyst(
                  {
                    gmbAddressesIds: this.worstGmbAddresses
                      .map(({ gmb_address_id }) => gmb_address_id)
                      .join(",")
                  },
                  "lowest_rated_locations"
                )
            }
          },
          layout: { x: 1, y: 3, w: 2, h: 2, i: "5" },
          planRestrictions: this.canMonitorRatingsAndReports
        },
        {
          component: {
            name: "ValueWidget",
            props: {
              name: "postsCount",
              value: this.postsCount.value,
              increaseValue: this.postsCount.change,
              loading: this.loadingPostsCount,
              actionHandler: () => this.goToPostsAdd({}, "num_posts_added")
            }
          },
          layout: { x: 0, y: 4, w: 1, h: 1, i: "6" },
          planRestrictions: this.canPublishMarketingPublications
        },
        {
          component: {
            name: "ValueWidget",
            props: {
              name: "viewsCount",
              value: this.viewsCount.value,
              increaseValue: this.viewsCount.change,
              showIncreaseIndicator: this.showIncreaseIndicator,
              loading: this.loadingStats,
              actionHandler: () =>
                this.goToAnalyticsGmbAddresses("#viewsCount", "stats_views")
            }
          },
          layout: { x: 0, y: 5, w: 1, h: 1, i: "7" },
          planRestrictions: this.canMonitorGmbActionsAndReports
        },
        {
          component: {
            name: "ValueWidget",
            props: {
              name: "actionsPhone",
              value: this.actionsPhone.value,
              increaseValue: this.actionsPhone.change,
              showIncreaseIndicator: this.showIncreaseIndicator,
              loading: this.loadingStats,
              actionHandler: () =>
                this.goToAnalyticsGmbAddresses("#actionsPhone", "actions_phone")
            }
          },
          layout: { x: 1, y: 5, w: 1, h: 1, i: "8" },
          planRestrictions: this.canMonitorGmbActionsAndReports
        },
        {
          component: {
            name: "ValueWidget",
            props: {
              name: "actionsDrivingDirections",
              value: this.actionsDrivingDirections.value,
              increaseValue: this.actionsDrivingDirections.change,
              showIncreaseIndicator: this.showIncreaseIndicator,
              loading: this.loadingStats,
              actionHandler: () =>
                this.goToAnalyticsGmbAddresses(
                  "#actionsDrivingDirections",
                  "actions_navigation"
                )
            }
          },
          layout: { x: 2, y: 5, w: 1, h: 1, i: "9" },
          planRestrictions: this.canMonitorGmbActionsAndReports
        },
        {
          component: {
            name: "ValueWidget",
            props: {
              name: "actionsWebsite",
              value: this.actionsWebsite.value,
              increaseValue: this.actionsWebsite.change,
              showIncreaseIndicator: this.showIncreaseIndicator,
              loading: this.loadingStats,
              actionHandler: () =>
                this.goToAnalyticsGmbAddresses("#actionsWebsite", "actions_www")
            }
          },
          layout: { x: 2, y: 6, w: 1, h: 1, i: "10" },
          planRestrictions: this.canMonitorGmbActionsAndReports
        },
        {
          component: {
            name: "LowestQualityAddressesWidget",
            props: {
              data: this.lowestQualityGmbAddresses,
              loading: this.loadingAddresses,
              actionHandler: () =>
                this.goToLocationsQuality("lowest_quality_locations")
            }
          },
          layout: { x: 0, y: 6, w: 2, h: 2, i: "11" },
          featureFlag: FEATURE_FLAG.CN_6145_FACILITIES_QUALITY
        }
      ];
    },
    visibleWidgets() {
      const visibleWidgets = this.widgets
        .filter(widget =>
          "planRestrictions" in widget ? widget.planRestrictions : true
        )
        .filter(widget =>
          "featureFlag" in widget
            ? isFeatureFlagEnabled(widget.featureFlag)
            : true
        );

      return this.repositionWidgets(visibleWidgets);
    },
    layout() {
      return this.widgets.map(({ layout }) => layout);
    },
    canMonitorGmbActionsAndReports() {
      return planRestrictionsCheck(
        PLAN_RESTRICTIONS.CAN_MONITOR_GMB_ACTIONS_AND_REPORTS
      );
    },
    canMonitorRatingsAndReports() {
      return planRestrictionsCheck(
        PLAN_RESTRICTIONS.CAN_MONITOR_RATINGS_AND_REPORTS
      );
    },
    canPublishMarketingPublications() {
      return planRestrictionsCheck(
        PLAN_RESTRICTIONS.CAN_PUBLISH_MARKETING_PUBLICATIONS
      );
    }
  },

  created() {
    this.getAllStatsWithParams();
  },

  mounted() {
    this.mixin__setGtmEngagementTimeouts();
  },

  destroyed() {
    this.resetFacilitiesStore();
    this.resetFacilitiesGroupsStore();
  },

  methods: {
    ...mapActions({
      resetFacilitiesStore: "facilities/resetStore",
      resetFacilitiesGroupsStore: "facilitiesGroups/resetStore"
    }),

    getAllStatsWithParams() {
      if (this.canMonitorRatingsAndReports) {
        this.getReviewsStats();
      }
      if (this.canMonitorGmbActionsAndReports) {
        this.getInsights();
      }
      if (this.canPublishMarketingPublications) {
        this.getLocalPostsStats();
      }
      this.getGmbAddressesStats();
    },

    async getGmbAddressesStats() {
      this.loadingAddresses = true;
      const result = await serviceHandler(
        DashboardService.getGmbAddressesStats(this.queryParams)
      );

      if (result) {
        if (result.best_gmb_addresses) {
          this.bestGmbAddresses = result.best_gmb_addresses;
        }

        if (result.worst_gmb_addresses) {
          this.worstGmbAddresses = result.worst_gmb_addresses;
        }

        if (result.lowest_quality_gmb_addresses) {
          this.lowestQualityGmbAddresses = result.lowest_quality_gmb_addresses;
        }
      }

      this.loadingAddresses = false;
    },

    async getReviewsStats() {
      this.loadingRatings = true;
      const result = await serviceHandler(
        DashboardService.getReviewsStats(this.queryParams)
      );

      if (result) {
        if (result.avg_rating) {
          this.averageRating = result.avg_rating;
        }

        if (result.count) {
          this.ratingCount = result.count;
        }

        if (result.rating_distribution) {
          this.ratingDistribution = result.rating_distribution;
        }

        if (result.replies_percentage) {
          this.repliesPercentage = result.replies_percentage;
        }
      }

      this.loadingRatings = false;
    },

    async getInsights() {
      this.loadingStats = true;
      const result = await serviceHandler(
        DashboardService.getInsights(this.queryParams)
      );

      if (result) {
        if (result.views_count) {
          this.viewsCount = result.views_count;
        }

        if (result.queries_count) {
          this.queriesCount = result.queries_count;
        }

        if (result.website_clicks) {
          this.actionsWebsite = result.website_clicks;
        }

        if (result.call_clicks) {
          this.actionsPhone = result.call_clicks;
        }

        if (result.business_direction_requests) {
          this.actionsDrivingDirections = result.business_direction_requests;
        }

        if (result.queries) {
          this.queries = result.queries;
        }
      }

      this.loadingStats = false;
    },

    async getLocalPostsStats() {
      this.loadingPostsCount = true;
      const result = await serviceHandler(
        DashboardService.getLocalPostsStats(this.queryParams)
      );

      if (result && result.posts_count) {
        this.postsCount = this.isDemoMode
          ? {
              value: getRandomBetween(10, 120),
              change: getRandomBetween(5, 60)
            }
          : result.posts_count;
      }

      this.loadingPostsCount = false;
    },

    onFiltersChange({
      gmbAddressesIds = this.gmbAddressesIds,
      cities = this.cities,
      facilitiesGroupsIds = this.facilitiesGroupsIds
    }) {
      this.gmbAddressesIds = gmbAddressesIds;
      this.cities = cities;
      this.facilitiesGroupsIds = facilitiesGroupsIds;
      this.getAllStatsWithParams();
    },

    closeFilterDialog() {
      this.filterDialog = false;
    },

    goToReviewsAnalyst(params = {}, component) {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.DASHBOARD_SHOW_DETAILS,
        component
      });

      const query = {
        dateFrom: this.queryParams.firstDateFrom,
        dateTo: this.queryParams.firstDateTo,
        ...params
      };

      this.$router.push({ name: "reviews-analyst", query });
    },

    goToReviewsList() {
      const query = {
        dateFrom: this.queryParams.firstDateFrom,
        dateTo: this.queryParams.firstDateTo
      };
      this.$router.push({ name: "reviews-list", query });
    },

    goToAnalyticsGmbAddresses(hash, component) {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.DASHBOARD_SHOW_DETAILS,
        component
      });

      const query = {
        dateFrom: this.queryParams.firstDateFrom,
        dateTo: this.queryParams.firstDateTo
      };
      this.$router.push({
        name: "analytics-gmb-addresses",
        query: query,
        hash
      });
    },

    goToPostsAdd() {
      this.$router.push({ name: "posts-add" });
    },

    goToLocationsQuality(component) {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.DASHBOARD_SHOW_DETAILS,
        component
      });

      this.$router.push({
        name: "locations-quality",
        query: {
          sort: "asc"
        }
      });
    },

    repositionWidgets(widgets) {
      const gridWidth = 3;
      const gridHeight = 7;
      let freeSpots = new Set();

      // Step 1: Initialize all free spots in the grid
      for (let y = 0; y < gridHeight; y++) {
        for (let x = 0; x < gridWidth; x++) {
          freeSpots.add(`${x}:${y}`);
        }
      }

      // Step 2: Place each visible widget in the first fitting free spot
      for (let widget of widgets) {
        const { w, h } = widget.layout;

        // Find the first available spot that can accommodate the widget's width and height
        for (let spot of freeSpots) {
          let [x, y] = spot.split(":").map(Number);

          if (x + w <= gridWidth && y + h <= gridHeight) {
            widget.layout.x = x;
            widget.layout.y = y;

            // Mark spots occupied by this widget as unavailable
            for (let yy = y; yy < y + h; yy++) {
              for (let xx = x; xx < x + w; xx++) {
                freeSpots.delete(`${xx}:${yy}`);
              }
            }
            break;
          }
        }
      }

      return widgets;
    }
  }
};
</script>

<style lang="scss" scoped>
.dashboard-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: $spacing;
  margin-bottom: $spacing;

  .welcome {
    color: $grey-darker;
    font-weight: bold;
    @include font-size(30px);
    line-height: 37px;
  }

  .filters {
    margin-left: auto;

    .v-btn {
      margin-left: $spacing;
    }
  }
}

.dashboard-grid {
  margin-right: $spacing * -1.5;
  margin-left: $spacing * -1.5;

  .dashboard-grid-item {
    padding: $spacing;
  }
}
</style>
